import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { describeFederatedRefusal } from "@chatable/chat-client/federated-auth";
import { createLogger } from "../../utils/logger";
import { sanitizeRedirectPath } from "../../utils/navigation";

// Circuit breaker constants for OAuth retry protection
const MAX_OAUTH_ATTEMPTS = 3;
const OAUTH_ATTEMPT_WINDOW_MS = 5 * 60 * 1000; // 5 minutes
const OAUTH_STORAGE_KEY = "oauth_attempts";
const OAUTH_TIMESTAMP_KEY = "oauth_last_attempt";
const OAUTH_STATE_DATA_KEY = "oauth_state_data";

interface OAuthAttemptData {
  count: number;
  lastAttempt: number;
}

interface OAuthStateData {
  state?: string;
  redirectTo?: string;
  returnTo?: string;
}

const getRedirectTargets = (stateParam: string | null) => {
  if (typeof window === "undefined") {
    return { success: "/", failure: "/" };
  }

  try {
    const raw = window.sessionStorage.getItem(OAUTH_STATE_DATA_KEY);
    const fallback = { success: "/", failure: "/" };

    if (!raw) {
      return fallback;
    }

    const stateData = JSON.parse(raw) as OAuthStateData;
    if (!stateParam || stateData.state !== stateParam) {
      return fallback;
    }

    const success = sanitizeRedirectPath(stateData.redirectTo || stateData.returnTo);
    const failure = sanitizeRedirectPath(stateData.returnTo || stateData.redirectTo);

    return { success, failure };
  } catch {
    return { success: "/", failure: "/" };
  }
};

// Create logger for OAuth callback
const logger = createLogger("OAuthCallback");

export const OAuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { handleOAuthCallback } = useAuth();
  const [isProcessing, setIsProcessing] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hasProcessed = useRef(false);

  // Circuit breaker: Check if we've exceeded retry attempts
  const checkCircuitBreaker = (): boolean => {
    try {
      if (typeof window === "undefined" || !window.sessionStorage) {
        return true; // Allow if sessionStorage is not available
      }

      const storedData = window.sessionStorage.getItem(OAUTH_STORAGE_KEY);
      if (!storedData) return true; // No previous attempts, allow

      const attemptData: OAuthAttemptData = JSON.parse(storedData);
      const now = Date.now();
      const timeSinceLastAttempt = now - attemptData.lastAttempt;

      // Reset counter if enough time has passed
      if (timeSinceLastAttempt > OAUTH_ATTEMPT_WINDOW_MS) {
        window.sessionStorage.removeItem(OAUTH_STORAGE_KEY);
        window.sessionStorage.removeItem(OAUTH_TIMESTAMP_KEY);
        return true;
      }

      // Check if we've exceeded max attempts
      if (attemptData.count >= MAX_OAUTH_ATTEMPTS) {
        logger.warn("OAuth circuit breaker triggered", {
          attemptCount: attemptData.count,
          timeSinceLastAttemptSeconds: Math.round(timeSinceLastAttempt / 1000),
        });
        return false;
      }

      return true;
    } catch {
      // If there's an error reading storage, allow the attempt
      return true;
    }
  };

  // Record OAuth attempt for circuit breaker
  const recordOAuthAttempt = (): void => {
    try {
      if (typeof window === "undefined" || !window.sessionStorage) {
        return; // Silently fail if sessionStorage is not available
      }

      const storedData = window.sessionStorage.getItem(OAUTH_STORAGE_KEY);
      const now = Date.now();

      let attemptData: OAuthAttemptData;
      if (storedData) {
        attemptData = JSON.parse(storedData);
        attemptData.count += 1;
        attemptData.lastAttempt = now;
      } else {
        attemptData = { count: 1, lastAttempt: now };
      }

      window.sessionStorage.setItem(OAUTH_STORAGE_KEY, JSON.stringify(attemptData));
      window.sessionStorage.setItem(OAUTH_TIMESTAMP_KEY, now.toString());
    } catch {
      // Silently fail if sessionStorage is not available
    }
  };

  // Clear OAuth attempt tracking on success
  const clearOAuthAttempts = (): void => {
    try {
      if (typeof window === "undefined" || !window.sessionStorage) {
        return; // Silently fail if sessionStorage is not available
      }

      window.sessionStorage.removeItem(OAUTH_STORAGE_KEY);
      window.sessionStorage.removeItem(OAUTH_TIMESTAMP_KEY);
    } catch {
      // Silently fail if sessionStorage is not available
    }
  };

  useEffect(() => {
    // Prevent multiple executions due to React strict mode or re-renders
    if (hasProcessed.current) {
      return;
    }

    const processCallback = async () => {
      let redirectTargets = { success: "/", failure: "/" };

      try {
        hasProcessed.current = true;
        setIsProcessing(true);
        setError(null);

        logger.debug("🔐 OAuth callback initiated", {
          timestamp: new Date().toISOString(),
        });

        // Circuit breaker check
        if (!checkCircuitBreaker()) {
          const timeRemaining = Math.ceil(OAUTH_ATTEMPT_WINDOW_MS / 1000 / 60);
          throw new Error(
            `Too many OAuth attempts. Please wait ${timeRemaining} minutes before trying again.`,
          );
        }

        // Record this attempt
        recordOAuthAttempt();

        // Extract parameters from URL
        const code = searchParams.get("code");
        const state = searchParams.get("state");
        const error = searchParams.get("error");
        const errorDescription = searchParams.get("error_description");
        redirectTargets = getRedirectTargets(state);

        logger.debug("URL parameters", {
          code: code ? `${code.substring(0, 8)}...` : null,
          state: state ? `${state.substring(0, 8)}...` : null,
          error,
          errorDescription,
        });

        // Handle OAuth errors (user cancelled, access denied, etc.)
        if (error) {
          logger.debug("OAuth provider error detected", {
            error,
            errorDescription,
          });

          if (error === "access_denied") {
            // User cancelled - don't count as failed attempt
            clearOAuthAttempts();
            navigate(redirectTargets.failure, {
              replace: true,
              state: { message: "Sign in was cancelled" },
            });
            return;
          }

          // Other OAuth errors
          throw new Error(
            describeFederatedRefusal(errorDescription ?? "") ?? errorDescription ?? error,
          );
        }

        // Validate required parameters
        if (!code || !state) {
          throw new Error("Missing required OAuth parameters (code or state)");
        }

        logger.debug("Processing OAuth callback");

        // Process the OAuth callback
        await handleOAuthCallback(code, state);

        logger.debug("OAuth callback successful");

        // Clear attempt tracking on success
        clearOAuthAttempts();

        // Redirect to dashboard on success
        navigate(redirectTargets.success, {
          replace: true,
          state: { message: "Successfully signed in!" },
        });
      } catch (err) {
        logger.error("❌ OAuth callback error", { error: err });

        const errorMessage = err instanceof Error ? err.message : "Authentication failed";
        setError(errorMessage);

        // Determine if this is a retryable error
        const isRetryableError =
          errorMessage.includes("network") ||
          errorMessage.includes("timeout") ||
          errorMessage.includes("fetch");

        // Redirect to home with error after a delay
        const redirectDelay = isRetryableError ? 5000 : 3000;
        window.setTimeout(() => {
          navigate(redirectTargets.failure, {
            replace: true,
            state: {
              error: `OAuth authentication failed: ${errorMessage}`,
            },
          });
        }, redirectDelay);
      } finally {
        setIsProcessing(false);
      }
    };

    processCallback();
  }, [searchParams, handleOAuthCallback, navigate]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
          <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
            <svg
              className="w-6 h-6 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.864-.833-2.634 0L3.098 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-gray-900 text-center mb-2">
            Authentication Failed
          </h2>
          <p className="text-gray-600 text-center mb-4">{error}</p>
          <p className="text-sm text-gray-500 text-center">
            Redirecting you back to the main page...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
        <div className="flex items-center justify-center w-12 h-12 mx-auto bg-blue-100 rounded-full mb-4">
          <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
        </div>
        <h2 className="text-lg font-semibold text-gray-900 text-center mb-2">
          {isProcessing ? "Completing sign in..." : "Sign in successful!"}
        </h2>
        <p className="text-gray-600 text-center">
          {isProcessing
            ? "Please wait while we complete your sign-in..."
            : "Redirecting you to the dashboard..."}
        </p>
      </div>
    </div>
  );
};

export default OAuthCallback;
